import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import SimulatedBanner from "@/components/SimulatedBanner";
import GalleryPicker, { assetUrl } from "@/components/GalleryPicker";
import {
  Mail, Plus, Users, Send, Trash2, ChevronRight, AtSign, Loader2, Clock, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  draft: "Borrador",
  scheduled: "Programada",
  sending: "Enviando",
  sent: "Enviada",
  failed: "Fallida",
};

const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  scheduled: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  sending: "bg-blue-50 text-blue-700 border-blue-200",
  sent: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function fromLocalInputValue(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export default function EmailMarketing() {
  const { isMaster, hasModule } = useAuth();
  const canUseGallery = hasModule("motor_creativo");
  const [tab, setTab] = useState("campaigns");

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="email-marketing-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <Mail className="h-8 w-8 text-brand-emerald" />
          Email Marketing
          <SimulatedBanner compact />
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          Gestione cuentas de envío, listas de contactos y campañas. Los envíos se
          simulan en esta fase — el <strong>pixel de tracking de apertura es real</strong>
          y ya devuelve métricas verdaderas.
        </p>
      </div>

      <SimulatedBanner />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="email-tabs">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">
            Campañas
          </TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists">
            Listas
          </TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">
            Cuentas
          </TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns" className="mt-6">
          <CampaignsTab canUseGallery={canUseGallery} />
        </TabsContent>
        <TabsContent value="lists" className="mt-6">
          <ListsTab />
        </TabsContent>
        <TabsContent value="accounts" className="mt-6">
          <AccountsTab isMaster={isMaster} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Cuentas
// -----------------------------------------------------------------------------
function AccountsTab({ isMaster }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/accounts");
      setAccounts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/email/accounts", form);
      toast.success("Cuenta conectada (modo simulado)");
      setOpen(false);
      setForm({ email: "", display_name: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Desconectar ${a.email}?`)) return;
    try {
      await api.delete(`/email/accounts/${a.id}`);
      toast.success("Cuenta desconectada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-stone-500">
          Cuentas conectadas para el envío. Los usuarios sin rol Master no verán credenciales.
        </p>
        {isMaster && (
          <Button
            onClick={() => setOpen(true)}
            data-testid="account-add-btn"
            className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Conectar cuenta
          </Button>
        )}
      </div>

      {loading ? (
        <div className="card-elevated p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500">
          Sin cuentas de correo conectadas.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <div
              key={a.id}
              className="card-elevated p-5 flex items-center justify-between"
              data-testid={`account-row-${a.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg bg-brand-emerald-soft text-brand-emerald-dark flex items-center justify-center">
                  <AtSign className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-brand-ink flex items-center gap-2">
                    {a.email}
                    <SimulatedBanner compact />
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {a.display_name} · conectada {fmt(a.connected_at)}
                  </div>
                </div>
              </div>
              {isMaster && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(a)}
                  className="text-destructive hover:bg-destructive/10"
                  data-testid={`account-delete-${a.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Conectar cuenta de correo</DialogTitle>
            <DialogDescription>Modo simulado — no se contactará ningún proveedor externo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Correo remitente</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Nombre a mostrar (opcional)</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="account-display-name-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="account-submit" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
                {saving ? "Conectando…" : "Conectar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Listas
// -----------------------------------------------------------------------------
function ListsTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/lists");
      setLists(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/email/lists", form);
      toast.success("Lista creada");
      setOpen(false);
      setForm({ name: "", description: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar la lista "${l.name}" y todos sus contactos?`)) return;
    try {
      await api.delete(`/email/lists/${l.id}`);
      toast.success("Lista eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-stone-500">Listas de contactos disponibles.</p>
        <Button
          onClick={() => setOpen(true)}
          data-testid="list-add-btn"
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nueva lista
        </Button>
      </div>

      {loading ? (
        <div className="card-elevated p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : lists.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500">Sin listas creadas.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lists.map((l) => (
            <div key={l.id} className="card-elevated p-5" data-testid={`list-row-${l.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-brand-emerald-soft text-brand-emerald-dark flex items-center justify-center">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-serif-display text-lg font-semibold text-brand-ink">{l.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {l.contact_count} contacto{l.contact_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(l)} className="text-destructive hover:bg-destructive/10" data-testid={`list-delete-${l.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {l.description && (
                <p className="text-sm text-stone-600 mb-3">{l.description}</p>
              )}
              <Link
                to={`/email-marketing/listas/${l.id}`}
                className="text-sm text-brand-emerald-dark hover:underline flex items-center gap-1"
                data-testid={`list-open-${l.id}`}
              >
                Ver contactos <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="list-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Nueva lista</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Nombre</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="list-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Descripción (opcional)</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="list-description-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="list-submit" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                {saving ? "Creando…" : "Crear lista"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Campañas
// -----------------------------------------------------------------------------
function CampaignsTab({ canUseGallery }) {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    account_id: "",
    list_id: "",
    subject: "",
    body_html: "<p>Hola {{nombre}},</p>\n<p>Bienvenido a FStudy.</p>",
    scheduled_at: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, l] = await Promise.all([
        api.get("/email/campaigns"),
        api.get("/email/accounts"),
        api.get("/email/lists"),
      ]);
      setCampaigns(c.data);
      setAccounts(a.data);
      setLists(l.data);
      if (!form.account_id && a.data.length) setForm((f) => ({ ...f, account_id: a.data[0].id }));
      if (!form.list_id && l.data.length) setForm((f) => ({ ...f, list_id: l.data[0].id }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (action, e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
      toast.error("Complete nombre, asunto y contenido");
      return;
    }
    if (!form.account_id || !form.list_id) {
      toast.error("Seleccione cuenta y lista");
      return;
    }
    if (action === "schedule" && !form.scheduled_at) {
      toast.error("Indique fecha de programación");
      return;
    }
    setSaving(true);
    try {
      await api.post("/email/campaigns", {
        name: form.name.trim(),
        account_id: form.account_id,
        list_id: form.list_id,
        subject: form.subject.trim(),
        body_html: form.body_html,
        action,
        scheduled_at: action === "schedule" ? fromLocalInputValue(form.scheduled_at) : null,
      });
      const msg = { send_now: "Campaña enviada (simulada)", schedule: "Campaña programada", draft: "Borrador guardado" }[action];
      toast.success(msg);
      setOpen(false);
      setForm({ ...form, name: "", subject: "", scheduled_at: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar la campaña "${c.name}"?`)) return;
    try {
      await api.delete(`/email/campaigns/${c.id}`);
      toast.success("Campaña eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const sendNow = async (c) => {
    try {
      await api.post(`/email/campaigns/${c.id}/send`);
      toast.success("Campaña enviada (simulada)");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-stone-500">Diseñe y envíe campañas simuladas con tracking real.</p>
        <Button
          onClick={() => setOpen(true)}
          data-testid="campaign-add-btn"
          disabled={accounts.length === 0 || lists.length === 0}
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nueva campaña
        </Button>
      </div>
      {(accounts.length === 0 || lists.length === 0) && (
        <div className="text-xs text-stone-500 -mt-2">
          Necesita al menos 1 cuenta y 1 lista para crear campañas.
        </div>
      )}

      {loading ? (
        <div className="card-elevated p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500">Sin campañas todavía.</div>
      ) : (
        <div className="card-elevated overflow-hidden">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/60">
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Lista</TableHead>
                <TableHead>Métricas</TableHead>
                <TableHead>Fechas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <TableCell>
                    <Link to={`/email-marketing/campanas/${c.id}`} className="font-medium text-brand-ink hover:underline">
                      {c.name}
                    </Link>
                    <div className="text-xs text-stone-500 mt-0.5">{c.subject}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`border font-semibold ${STATUS_STYLES[c.status] || ""}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{c.list_name}</TableCell>
                  <TableCell>
                    {c.status === "sent" ? (
                      <div className="text-xs">
                        <div><strong>{c.stats?.sent || 0}</strong> enviados</div>
                        <div className="text-brand-emerald-dark"><strong>{c.stats?.opened || 0}</strong> abiertos</div>
                      </div>
                    ) : <span className="text-xs text-stone-400">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-stone-500">
                    {c.scheduled_at && <div>Programada: {fmt(c.scheduled_at)}</div>}
                    {c.sent_at && <div>Enviada: {fmt(c.sent_at)}</div>}
                    {!c.scheduled_at && !c.sent_at && <div>Creada: {fmt(c.created_at)}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      {c.status !== "sent" && c.status !== "sending" && (
                        <Button size="icon" variant="ghost" onClick={() => sendNow(c)} className="text-brand-emerald-dark" data-testid={`campaign-send-${c.id}`} title="Enviar ahora">
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      <Link to={`/email-marketing/campanas/${c.id}`}>
                        <Button size="icon" variant="ghost" data-testid={`campaign-open-${c.id}`} title="Ver detalle">
                          <ListChecks className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button size="icon" variant="ghost" onClick={() => remove(c)} className="text-destructive hover:bg-destructive/10" data-testid={`campaign-delete-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Nueva campaña</DialogTitle>
            <DialogDescription>Envíe ahora, programe para más tarde o guarde como borrador.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={(e) => submit("send_now", e)}>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Nombre interno</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Cuenta remitente</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger data-testid="campaign-account-select"><SelectValue placeholder="Seleccione cuenta" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Lista destino</Label>
                <Select value={form.list_id} onValueChange={(v) => setForm({ ...form, list_id: v })}>
                  <SelectTrigger data-testid="campaign-list-select"><SelectValue placeholder="Seleccione lista" /></SelectTrigger>
                  <SelectContent>
                    {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.contact_count})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Asunto</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="campaign-subject-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Cuerpo HTML</Label>
                <GalleryPicker
                  enabled={canUseGallery}
                  testid="campaign-gallery-picker"
                  onSelect={(piece) => {
                    const url = assetUrl(piece.file_url);
                    const tag = `<p><img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:6px" /></p>\n`;
                    setForm((f) => ({ ...f, body_html: tag + f.body_html }));
                    toast.success("Pieza añadida al cuerpo del correo");
                  }}
                />
              </div>
              <Textarea rows={7} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" data-testid="campaign-body-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Programar para (opcional)</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="campaign-schedule-input" />
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button type="button" variant="ghost" disabled={saving} onClick={(e) => submit("draft", e)} data-testid="campaign-draft-btn">
                Guardar borrador
              </Button>
              <Button type="button" variant="outline" disabled={saving} onClick={(e) => submit("schedule", e)} data-testid="campaign-schedule-btn">
                <Clock className="h-4 w-4 mr-2" /> Programar
              </Button>
              <Button type="submit" disabled={saving} data-testid="campaign-send-now-btn" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
                <Send className="h-4 w-4 mr-2" /> Enviar ahora
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
